import { DOCUMENT, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNl from '@angular/common/locales/nl';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideTranslateService, TranslateCompiler } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { authInterceptor } from './interceptors/auth.interceptor';

// Dutch formatting for dates/numbers app-wide (DatePipe, DecimalPipe, Material datepicker).
// UI text stays translated via ngx-translate — this only affects locale-sensitive formatting.
registerLocaleData(localeNl);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor])),
    { provide: LOCALE_ID, useValue: 'nl' },
    { provide: MAT_DATE_LOCALE, useValue: 'nl' },
    // Datepickers need a DateAdapter at the root. Screens that import MatNativeDateModule get one in their
    // own standalone injector, but a datepicker inside a MatDialog opens its calendar in a CDK overlay that
    // resolves against the environment injector instead, where that import is invisible — the calendar then
    // fails with NG0201 and simply never opens. Providing it here covers every datepicker, in a dialog or not.
    // It reads MAT_DATE_LOCALE above, so the calendar formats Dutch like the rest of the app.
    provideNativeDateAdapter(),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideAppInitializer(() => {
      const apiKey = environment.googleMapsApiKey;
      if (!apiKey || (window as { google?: { maps?: unknown } }).google?.maps) return;
      const doc = inject(DOCUMENT);
      return new Promise<void>((resolve) => {
        const script = doc.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        doc.head.appendChild(script);
      });
    }),
    provideTranslateService({
      fallbackLang: 'en',
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    provideTranslateHttpLoader({
      prefix: '/i18n/',
      suffix: '.json',
    }),
  ],
};
